// Copyright (c) Docugami, Inc. All rights reserved.
import { createTheme, IDetailsColumnStyleProps, IDetailsColumnStyles, IStyleFunctionOrObject, loadTheme } from "@fluentui/react";
import { dgFontFamily, primaryFontExpanded } from "components/common/Fonts";

export const standardShadow = "0 0 10px grey";
export const smallShadow = "0 0 5px grey";
export const modalShadow = "0px 0px 10px rgba(0, 0, 0, 0.2)";

export const actionBlue = '#0085FF';
export const lightActionBlue = '#0085FF80';
export const appContentColor = "#58595B";
export const backgroundWhite = "#F8FAFB";
export const userConfirmedGreen = "#A3F090";
export const themeYellow = "#f2df30";
export const themeBlue = "#56CCF2";
export const themeRed = "#FF3F3F";
export const themeOrange = '#FD9800';
export const themeGreen = '#A3F090';
export const themeWhite = '#FFFFFF';
export const themePink = '#FF80DC';
export const themePinkLight = "#FFC2EE";
export const backgroundGray = `#EBEBEB`;
export const themeRedDark = "#E03535";
export const warningRed = "#FF6868";
export const landingPageBackground = "#F6F6F6";

export const highConfColor = "#FEFF997F";
export const lowConfColor = "#FEFF9919";
export const confirmedColor = "#C6FF99";
export const emptyColor = "#E5E5E5";
export const homeGray = "#5E675A";
export const emptyGray = "#D0CDCD";
export const greyText = "#999999";
export const charcoalGrayText = "#777777";
export const backgroundPanel = "#F3F3F3";
export const disabledGreyText = "#99999940";
export const disabledDarkGreyText = "#C8C8C8"
export const highlightYellow = "#FEC705";
export const sdvPreviewHighlightYellow = "#FFE177";
export const hightlightBlue = "#DCF3FA";
export const lightHighlightBlue = "#d9f6ff";

export const subheadGrey = '#ECECEC';
export const appBackgroundGrey = '#F6F6F6';
export const filterInputGrey = '#DFDFDF';
export const lightButtonGray = '#CECECE';
export const cellBackgroundGray = '#EAEAEA';
export const labelGray = '#A1A1A1';

export const reportEntryHighlightColor = "#AAE6F980"

export const chunkHighlightUnconfirmedColor = "#FEFF99";
export const chunkHighlightUnconfirmedSelectionHintColor = "#FEFF9988";

export const chunkHoverColor = "#AAE6F980";
export const chunkHintColor = "#AAE6F980";
export const chunkSelectedColor = "#56CCF2BF";
export const chunkSelectedBorderColor = actionBlue
export const chunkGrabHandleBorderColor = actionBlue
export const chunkGrabHandleFillColor = "#FFFFFF"
export const machineGreen = '#B6EB82'
export const humanBlue = '#3ED1FF'
export const chunkGrabHandleColor = "#FF81DC";

export const editableTokenColor = '#ffe791';
export const hoveredTokenColor = '#ffbd42';
export const selectedEditTokenColor = 'darkorange';
export const editTokenColor = 'darkorange';

export const ChunkNavigationControlColor = "#FC04B6";
export const ChunkNavigationControlEnabledButtonColor = "white";
export const ChunkNavigationControlDisabledButtonColor = "#FF80DC";

export const tagPickerBackgroundColor = actionBlue
export const modalSelectionColor = actionBlue
export const modalSelectionColorDisabled = "#afd8ff"

export const clauseSelectedGreen = '#C4F891';
export const aaMissingClauseColor = "#A3F090";
export const aaVariantClauseColor = "#FEC705";
export const aaReviewClauseColor = "#FF4747";
export const aaCardSeparatorColor = "#C4C4C4";
export const aaTextColor = "#929292";
export const aaCardBorderColor = "#D9DEF0";

export const recommendedTagBgColor = "#F2F2F2";
export const hierarchyTagHighlightColor = "#DDDDDD";

export const primaryColorDark = "#34A32E";
export const primaryAccentColor = "#4BBF44"
export const themeRedButtonDisabled = "#FF3F3F40";
export const themeGreenButtonDisabled = "#4BBF4440";
export const themeBlueButtonDisabled = "#56CCF240";

export const evalMetricsHumanColor = "#AAE6F9";
export const evalMetricsHumanConfirmedColor = themeBlue;
export const evalMetricsConfirmedMachineColor = primaryAccentColor;
export const evalMetricsMachineColor = "#B9F9AA";

export const reportEvaluatingProgressBarColor = "#B9F9AA";

export const chunkToolbarColor = "#56CCF2"

export const reportTableBorderColor = "#C4C4C4";
export const reportTableHoverColor = "#FAFAFA";

export const primaryThemeSettings = {
  defaultFontStyle: { fontFamily: primaryFontExpanded },
  palette: {
    themePrimary: primaryAccentColor,
    themeLighterAlt: '#f9fdf9',
    themeLighter: '#e8f5e9',
    themeLight: '#d5edd6',
    themeTertiary: '#aedab0',
    themeSecondary: '#8dc990',
    themeDarkAlt: '#73ae76',
    themeDark: '#619364',
    themeDarker: '#486d49',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
  }
};

export const primaryTheme = loadTheme(primaryThemeSettings);

export const secondaryThemeSettings = {
  defaultFontStyle: { fontFamily: "'" + dgFontFamily + "', sans-serif" },
  palette: {
    themePrimary: primaryAccentColor,
    themeLighterAlt: '#fafdfa',
    themeLighter: '#eaf7eb',
    themeLight: '#d9f0da',
    themeTertiary: '#b5e2b7',
    themeSecondary: '#97d49a',
    themeDarkAlt: '#3DB535',
    themeDark: '#699d6c',
    themeDarker: '#4d744f',
    neutralLighterAlt: '#eeeeee',
    neutralLighter: '#eaeaea',
    neutralLight: '#e1e1e1',
    neutralQuaternaryAlt: '#d1d1d1',
    neutralQuaternary: '#c8c8c8',
    neutralTertiaryAlt: '#c0c0c0',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#f6f6f6',
  }
};

export const secondaryTheme = createTheme(secondaryThemeSettings);

export const headerThemeSettings = {
  defaultFontStyle: { fontFamily: primaryFontExpanded },
  palette: {
    themePrimary: '#ffffff',
    themeLighterAlt: '#767676',
    themeLighter: '#a6a6a6',
    themeLight: '#c8c8c8',
    themeTertiary: '#d0d0d0',
    themeSecondary: '#dadada',
    themeDarkAlt: '#eaeaea',
    themeDark: '#f4f4f4',
    themeDarker: '#f8f8f8',
    neutralLighterAlt: '#96d586',
    neutralLighter: '#94d284',
    neutralLight: '#8ec97f',
    neutralQuaternaryAlt: '#84bc76',
    neutralQuaternary: '#7eb371',
    neutralTertiaryAlt: '#79ac6c',
    neutralTertiary: '#c8c8c8',
    neutralSecondary: '#d0d0d0',
    neutralPrimaryAlt: '#dadada',
    neutralPrimary: '#ffffff',
    neutralDark: '#f4f4f4',
    black: '#f8f8f8',
    white: '#4BBF44',
  }
};

export const headerTheme = createTheme(headerThemeSettings);

export const transparentTheme = createTheme({
  palette: {
    white: '#0',
    neutralLighter: '#0',
    neutralLight: '#0',
    neutralQuaternaryAlt: '#0'
  }
});

export const transparentDetailsList = createTheme({
  palette: {
    ...primaryThemeSettings.palette,
    white: '#0',
    neutralLighter: '#e3e3e3',
    neutralLight: '#d8d8d8',
    neutralQuaternaryAlt: '#c6c6c6'
  }
});

export const detailsListHeaderStyle: IStyleFunctionOrObject<IDetailsColumnStyleProps, IDetailsColumnStyles> = {
  cellTitle: {
    color: greyText,
    textTransform: 'uppercase',
    padding: 0
  },
  cellName: {
    fontWeight: '500'
  }
};

export const chunkResizeGrabberSelectedColor = "#1EB473";
export const chunkResizeGrabberNotSelectedColor = primaryTheme.palette.themePrimary;

export const automationAnywhereColor = "#ff6a00";
export const zapierColor = "#ff4a00";
export const powerAutomateColor = "#1078FB";

export enum ZIndices {
  ExpandButton = 3,
  PropertiesPanel = 4,
  ExternalCanvasComponent = 5,
  ChunkDragVisual = 6,
  StatusBar = 10,
  StickyTableCellBase = 11
}
